import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/shared/components/PageHeader';
import ConfirmDialog from '@/shared/components/ConfirmDialog';
import RegionTable from '../components/RegionTable';
import { useDeleteRegion } from '../hooks/useRegionMutations';
import type { Region } from '@/shared/types';
import DynamicFilterBuilder from '@/shared/filtering/DynamicFilterBuilder';
import { ADMIN_FILTER_RESOURCES, type DynamicRelationLookups } from '@/shared/filtering/types';
import { useDynamicAdminList } from '@/shared/filtering/useDynamicAdminList';
import { countryApi } from '@/features/countries/api/country.api';

export default function RegionListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [deleteTarget, setDeleteTarget] = useState<Region | null>(null);

  const list = useDynamicAdminList<Region>(ADMIN_FILTER_RESOURCES.regions, { initialSortField: 'id', initialSortDir: 'asc' });
  const { data, isLoading } = list.searchQuery;
  const deleteMutation = useDeleteRegion();

  const relationLookups = useMemo<DynamicRelationLookups>(() => ({
    'country.id': {
      fetchFn: ({ search, page, size }) => countryApi.list({ page, size, nameEn: search }),
      mapOption: (c: any) => ({ value: c.id, label: c.nameEn || c.nameAr || `#${c.id}` }),
      placeholder: t('regions.allCountries'),
    },
  }), [t]);

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, { onSettled: () => setDeleteTarget(null) });
  };

  return (
    <div>
      <PageHeader title={t('regions.title')} actionLabel={t('regions.addNew')} onAction={() => navigate('/regions/create')} />
      <DynamicFilterBuilder resource={list.resource} metadata={list.metadataQuery.data} value={list.filters} onChange={list.setFilters} onReset={list.resetFilters} relationLookups={relationLookups} />
      <div style={{ marginTop: 16 }}>
        <RegionTable
          data={data?.content || []}
          loading={isLoading}
          sortField={list.sortField}
          sortDir={list.sortDir}
          onSort={list.handleSort}
          onEdit={(id) => navigate(`/regions/${id}/edit`)}
          onDelete={(item) => setDeleteTarget(item)}
          page={data?.currentPage ?? 0}
          pageSize={list.size}
          total={data?.totalItems ?? 0}
          onPageChange={list.setPage}
        />
      </div>
      <ConfirmDialog
        open={!!deleteTarget}
        title={t('regions.deleteTitle')}
        message={`${t('regions.deleteMessage')} "${deleteTarget?.nameEn}"?`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
